import { ReactNode, useEffect, useMemo } from 'react'
import { OptionsHelper, Permission, validateDateRange } from '../../../../../../_metronic/helpers'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  DateInput,
  IOption,
  MonthInput,
  TableFilter,
  TableFilters,
} from '../../../../../components'
import { useAuth } from '../../../../auth'
import { AbaRelatorio, AgrupamentoQuantitativo, FiltroCeae } from '../../core/_models'
import { competenciaValida, MAX_DIAS_PERIODO_RELATORIO } from '../../core/filtro'
import { useListFilters } from '../../core/ListFiltersProvider'

const OPCOES_CEAE: IOption<FiltroCeae>[] = [
  { value: 'ceae', label: 'Somente unidades CEAE' },
  { value: 'nao-ceae', label: 'Exceto unidades CEAE' },
]

const OPCOES_AGRUPAR: IOption<AgrupamentoQuantitativo>[] = [
  { value: 'especialidade', label: 'Especialidade' },
  { value: 'solicitante', label: 'Solicitante' },
]

type Props = {
  aba: AbaRelatorio
  /** Área à direita (exportações da aba visível). */
  actions?: ReactNode
}

/**
 * Barra de filtros dos relatórios: empresa (obrigatória no contrato), período — ou competência,
 * na aba Mensal — e unidades CEAE; "Agrupar por" só no Quantitativo. Quem não tem escopo
 * amplo (ADMIN) fica com a empresa do vínculo ativo, como no painel de Indicadores.
 */
export const RelatoriosListSearchComponent = ({ aba, actions }: Props) => {
  const { filters, setFilter, clearFilters } = useListFilters()
  const { currentUser } = useAuth()
  const grupo = currentUser?.grupo?.nome
  const escopoAmplo = grupo === Permission.SuperAdmin || grupo === Permission.SuperMedico
  const porCompetencia = aba === 'mensal'

  const empresasDoVinculo = useMemo<IOption<number>[]>(() => {
    const vinculos = currentUser?.vinculos?.filter((vinculo) => vinculo.ativo) ?? []
    return vinculos
      .map((vinculo) => vinculo.empresa)
      .filter((empresa): empresa is NonNullable<typeof empresa> => Boolean(empresa?.id))
      .map((empresa) => ({
        value: Number(empresa.id),
        label: empresa.nomeFantasia ?? empresa.nome ?? String(empresa.id),
      }))
      .filter((opcao, indice, lista) => lista.findIndex((i) => i.value === opcao.value) === indice)
  }, [currentUser])

  const empresaUnica = !escopoAmplo && empresasDoVinculo.length === 1

  // "Limpar filtros" também zera a empresa: quem só tem um vínculo a recebe de volta.
  useEffect(() => {
    if (empresaUnica && !filters.empresa) setFilter('empresa', empresasDoVinculo[0])
  }, [empresaUnica, empresasDoVinculo, filters.empresa, setFilter])

  const { error: erroPeriodo, limits } = useMemo(
    () =>
      validateDateRange(filters.dataInicio, filters.dataFim, {
        maxRangeDays: MAX_DIAS_PERIODO_RELATORIO,
      }),
    [filters.dataInicio, filters.dataFim]
  )
  const erroCompetencia =
    filters.competencia && !competenciaValida(filters.competencia)
      ? 'Informe a competência no formato mês/ano'
      : !filters.competencia
      ? 'Informe a competência'
      : undefined

  const empresaParams = useMemo(() => ({ onlyActive: false }), [])

  // Chips: só o que restringe o relatório além do obrigatório (empresa e período não saem).
  const activeFilters: ActiveFilter[] = filters.ceae
    ? [
        {
          key: 'ceae',
          label: 'Unidades',
          value: filters.ceae.label,
          onRemove: () => setFilter('ceae', undefined),
        },
      ]
    : []

  return (
    <TableFilters
      layout='stacked'
      actions={actions}
      footer={<ActiveFiltersBar filters={activeFilters} onClearAll={clearFilters} />}
    >
      <TableFilter label='Empresa (Núcleo)' htmlFor='relatorios-ic-empresa' wide>
        <CustomSelect
          inputId='relatorios-ic-empresa'
          placeholder='Selecione a empresa'
          value={filters.empresa}
          isDisabled={empresaUnica}
          isClearable={!empresaUnica}
          onChange={(empresa: IOption<number> | null) => setFilter('empresa', empresa ?? undefined)}
          fetchOptions={escopoAmplo ? OptionsHelper.getOptionsEmpresasAdmin : undefined}
          fetchOptionsParams={escopoAmplo ? empresaParams : undefined}
          options={escopoAmplo ? undefined : empresasDoVinculo}
        />
      </TableFilter>

      {porCompetencia ? (
        <TableFilter
          label='Competência'
          htmlFor='relatorios-ic-competencia'
          error={erroCompetencia}
        >
          <MonthInput
            id='relatorios-ic-competencia'
            value={filters.competencia}
            onChange={(e) => setFilter('competencia', e.target.value)}
            isInvalid={Boolean(erroCompetencia)}
          />
        </TableFilter>
      ) : (
        <>
          <TableFilter
            label='Data inicial'
            htmlFor='relatorios-ic-inicio'
            error={erroPeriodo || (!filters.dataInicio ? 'Selecione uma data inicial' : undefined)}
          >
            <DateInput
              id='relatorios-ic-inicio'
              value={filters.dataInicio}
              minDate={limits.startMin}
              maxDate={limits.startMax}
              isInvalid={Boolean(erroPeriodo) || !filters.dataInicio}
              onChange={(e) => setFilter('dataInicio', e.target.value)}
            />
          </TableFilter>
          <TableFilter
            label='Data final'
            htmlFor='relatorios-ic-fim'
            error={!filters.dataFim ? 'Selecione uma data final' : undefined}
          >
            <DateInput
              id='relatorios-ic-fim'
              value={filters.dataFim}
              minDate={limits.endMin}
              maxDate={limits.endMax}
              isInvalid={Boolean(erroPeriodo) || !filters.dataFim}
              onChange={(e) => setFilter('dataFim', e.target.value)}
            />
          </TableFilter>
        </>
      )}

      <TableFilter label='Unidades' htmlFor='relatorios-ic-ceae'>
        <CustomSelect
          inputId='relatorios-ic-ceae'
          placeholder='Todas as unidades'
          options={OPCOES_CEAE}
          value={filters.ceae}
          onChange={(ceae: IOption<FiltroCeae> | null) => setFilter('ceae', ceae ?? undefined)}
        />
      </TableFilter>

      {aba === 'quantitativo' && (
        <TableFilter label='Agrupar por' htmlFor='relatorios-ic-agrupar'>
          <CustomSelect
            inputId='relatorios-ic-agrupar'
            options={OPCOES_AGRUPAR}
            isClearable={false}
            value={OPCOES_AGRUPAR.find((opcao) => opcao.value === filters.agrupar)}
            onChange={(opcao: IOption<AgrupamentoQuantitativo> | null) =>
              setFilter('agrupar', opcao?.value ?? 'especialidade')
            }
          />
        </TableFilter>
      )}
    </TableFilters>
  )
}
